/**
 * Run engine — pre-click gate and success notice (pure).
 *
 * The Run buttons used to disable only when no scenario was active; a missing
 * workspace and a scope that resolves to 0 items were only reported after the
 * click (runActiveInstance). This returns the same reasons BEFORE the click,
 * so the button is disabled with the reason on screen. runActiveInstance keeps
 * its own checks as the backstop (the scope is re-resolved on every run).
 */

export interface EngineRunGateInput {
  projectId: string | null;
  hasActiveScenario: boolean;
  /** 'live' runs have no scope to resolve. */
  scopeIsLive: boolean;
  /** Label of the active Input Scope, for the message. */
  scopeLabel: string;
  /** Resolved scope item ids; null for live. */
  scopedItemIds: Set<string> | null;
  /** True while a non-live scope is still being resolved. */
  scopeResolving: boolean;
}

export const RUN_NO_WORKSPACE_REASON = 'No workspace found.';
export const RUN_NO_SCENARIO_REASON = 'No scenario selected. Create and activate a scenario first.';

export function engineRunBlockedReason(input: EngineRunGateInput): string | null {
  if (!input.projectId) return RUN_NO_WORKSPACE_REASON;
  if (!input.hasActiveScenario) return RUN_NO_SCENARIO_REASON;
  if (!input.scopeIsLive && !input.scopeResolving && (input.scopedItemIds?.size ?? 0) === 0) {
    return (
      `Input scope “${input.scopeLabel}” resolves to 0 items. ` +
      'Add members to the queue, pool or collection, or pick another scope.'
    );
  }
  return null;
}

/** One-line confirmation after a run is saved. */
export function engineRunSavedNotice(summary: { includedCount: number; totalWorkUnits: number }): string {
  return `Run saved — ${summary.includedCount} of ${summary.totalWorkUnits} work units included. ` +
    'Review the summary, then propose it as a plan version when it is ready.';
}
